import {
    ArrowRightCircle,
    BgCar,
    BgCity,
    BulbIcon,
    EyeIcon,
    EyeIconHovered, LinkForward,
    Pause,
    Play,
    Refresh,
    UsersIcon,
    WarningIcon,
    WireIcon
} from "@/components/icons";
import {Link} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {QueryObserverResult} from "@tanstack/react-query";
import {StatsResponseDto} from "@/lib/api";
import {StatsResponse} from "@/hooks/useStats";
import {useT} from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

const FollowLinkIcon = () => {
    return (
        <div className="card-icon-container" role="presentation">
            <span className="in-active-icon"><ArrowRightCircle color={"var(--color-green)"}/></span>
            <span className="active-icon"><ArrowRightCircle strokeWidth={1.8} color={"var(--color-green)"}/></span>
        </div>)
}

export function HeadBanner({
                               refetch,
                               stats,
                               loading,
                               error
                           }: {
    refetch: () => Promise<QueryObserverResult<StatsResponseDto, Error>>
    loading: boolean,
    error: Error | null,
    stats: StatsResponse,

}) {
    const t = useT();
    const {activeDevices, unsupportedDevices, offlineDevices, chargingDevices} = stats;
    const LS_BG_PAUSED = "dashboardBgPaused";

    const [bgPaused, setBgPaused] = useState(() => localStorage.getItem(LS_BG_PAUSED) !== "false");
    useEffect(() => localStorage.setItem(LS_BG_PAUSED, String(bgPaused)), [bgPaused]);

    return <div className="dashboard-cards-wrap">
        <div className="dashboard-bg-controls">
            <button
                type="button"
                onClick={refetch}
                className="dashboard-bg-toggle"
                title={t("operator.overview.refresh")}
                aria-label={t("operator.overview.refresh")}
            >
                <Refresh size={24} strokeWidth={1.5}/>
            </button>
            <button
                type="button"
                onClick={() => setBgPaused((prev) => !prev)}
                className="dashboard-bg-toggle"
                title={t(bgPaused ? "operator.overview.background_play" : "operator.overview.background_pause")}
                aria-label={t(bgPaused ? "operator.overview.background_play" : "operator.overview.background_pause")}
            >
                {bgPaused ? (
                    <Play size={24} strokeWidth={1.5}/>
                ) : (
                    <Pause size={24} strokeWidth={1.5}/>
                )}
            </button>
        </div>
        <div className="dashboard-cards-bg" style={bgPaused ? {animationPlayState: "paused"} : undefined}>
            <BgCity/>
            <BgCity/>
        </div>
        {error && (
            <div role="alert" data-testid="stats-error" className="text-sm text-error">
                {t("operator.overview.stats_unavailable", { message: errorMessage(t, error, { fallback: "operator.error.request_failed" }) })}
            </div>
        )}
        <div className="dashboard-cards-container">
            <div className="each-card-container">

                <div className="card-details">
                    <p className="text-xs uppercase text-slate-500">{t("operator.overview.active_units")}</p>
                    <h1 className="text-3xl text-primary">{activeDevices || 0}</h1>
                    <p className="text-xs text-slate-400">{t("operator.overview.active_units_hint")}</p>
                </div>
                <div className="card-image-wrap">
                    <UsersIcon className="card-image"/>
                    <div className="card-icons-wrap">
                        <Link to="/devices?status=standby,charging,connected,ready">
                            <FollowLinkIcon/>
                        </Link>

                    </div>
                </div>
            </div>
            <div className="each-card-container">

                <div className="card-details">
                    <p className="text-xs uppercase text-slate-500">{t("operator.overview.charging")}</p>
                    <h1 className="text-3xl text-teal-500">{chargingDevices}</h1>
                    <p className="text-xs text-slate-400/80">{t("operator.overview.charging_hint")}</p>
                </div>
                <div className="card-image-wrap">
                    <BulbIcon className="card-image"/>
                    <div className="card-icons-wrap">
                        <Link to="/devices?status=charging">
                            <FollowLinkIcon/>
                        </Link>
                    </div>
                </div>
            </div>
            <div className="each-card-container">

                <div className="card-details">
                    <p className="text-xs uppercase text-slate-500">{t("common.status.offline")}</p>
                    <h1 className="text-3xl text-slate-500">{offlineDevices}</h1>
                    <p className="text-xs text-slate-400">{t("operator.overview.offline_hint")}</p>
                </div>
                <div className="card-image-wrap">
                    <WireIcon className="card-image"/>
                    <div className="card-icons-wrap">
                        <Link to="/devices?status=offline">
                            <FollowLinkIcon/>
                        </Link>
                    </div>
                </div>
            </div>
            <div className="each-card-container">

                <div className="card-details">
                    <p className="text-xs uppercase text-slate-500">{t("operator.overview.need_attention")}</p>
                    <h1 className={`text-3xl ${unsupportedDevices > 0 ? "text-rose-500" : "text-slate-500"}`}>{unsupportedDevices}</h1>
                </div>
                <div className="card-image-wrap">
                    <WarningIcon className="card-image"/>
                    <div className="card-icons-wrap">
                        <Link to="/devices?status=unsupported,error,blocked">
                            <FollowLinkIcon/>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
        <BgCar/>
    </div>;
}